<template>
  <div>
    <label :for="selector" class="label">
      {{ label }}
      <HelpTooltip v-if="labelDescription">
        <template #title>
          {{ label }}
        </template>
        <template #description>
          {{ labelDescription }}
        </template>
      </HelpTooltip>
    </label>
    <textarea :id="selector" />
  </div>
</template>

<script setup lang="ts">
import { sanitizeRichText } from '@/helpers/textSanitizerHelpers'
import * as Sentry from '@sentry/browser'
import { useCurrentCompany } from '@/hooks/swr/useCurrentCompany'
import { HUGERTE_PRESETS } from '@/hugerte/hugerteConfig'
import { attachMediaDialogTooltips } from '@/hugerte/mediaDialogTooltips'
import { useI18n } from '@/hooks/useI18N'
import type { Editor } from 'hugerte'
import hugerte from 'hugerte'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import aiIconString from '../../assets/img/ai_icon.svg?raw'
import HelpTooltip from './HelpTooltip.vue'

type HugertePreset = 'default' | 'product' | 'news'

interface Props {
  name: string
  label?: string
  value?: string
  preset?: HugertePreset
  labelDescription?: string
  disabled?: boolean
}

interface InputWithNamePayload {
  name: string
  value: string
}

const props = withDefaults(defineProps<Props>(), {
  label: '',
  value: '',
  preset: 'default',
  labelDescription: '',
  disabled: false,
})

const emit = defineEmits<{
  input: [value: string]
  inputWithName: [payload: InputWithNamePayload]
  onAi: []
}>()

const i18n = useI18n()
const { data: currentCompany } = useCurrentCompany()
const editorElement = ref<Editor | null>(null)
// TODO: Refactor to use a more stable ID - maybe a UUID library or useId when Vue 3.5 is available
const selector = ref(`editor-${Math.random().toString(36).substring(2, 15)}`)

// HugeRTE rewrites the DOM after our input handler emits (e.g. &nbsp; -> space), so
// the live content can differ from our own emit. Treating that echo as an external
// change made setContent() reset the caret to the top (ENGMAN-416).
let lastSyncedValue: string | null = null

const hasCompanyAi = computed(() => {
  return currentCompany.value?.ai_features_enabled
})

const activePreset = computed<HugertePreset>(() => props.preset || 'default')

watch(
  () => props.value,
  (newValue) => {
    const incoming = newValue ?? ''
    // Our own emit echoing back; the editor already shows it
    if (incoming === lastSyncedValue) return
    lastSyncedValue = incoming

    const editor = editorElement.value?.[0]
    if (!editor) return
    // Sanitize both values before comparing to avoid false mismatches
    // caused by HTML formatting differences (whitespace, self-closing tags, etc.)
    if (sanitizeRichText(incoming) !== sanitizeRichText(editor.getContent())) {
      editor.setContent(incoming)
    }
  }
)

// Re-initialize editor when preset or disabled state changes
watch([activePreset, () => props.disabled], async () => {
  await destroyEditor()
  await initHugerte()
})

onMounted(async () => {
  await initHugerte()
})

// Without this the editor — and the tooltips and observer attached to it —
// outlive a route change or a v-if teardown.
onUnmounted(() => {
  void destroyEditor()
})

async function destroyEditor() {
  if (editorElement.value?.[0]) {
    try {
      editorElement.value[0].destroy()
    } catch (error) {
      Sentry.captureException(error)
    }
  }
  editorElement.value = null
}

async function initHugerte() {
  try {
    const configFile = HUGERTE_PRESETS[activePreset.value]

    editorElement.value = await hugerte.init({
      selector: `#${selector.value}`,
      content_css: false,
      skin: false,
      readonly: props.disabled,

      setup: (editor: Editor) => {
        if (hasCompanyAi.value) {
          setAiIcon(editor)
          setAiButton(editor)
        }

        attachMediaDialogTooltips(editor, {
          general: i18n.t('video.UPLOAD_OPTION_GENERAL_HELP'),
          embed: i18n.t('video.UPLOAD_OPTION_EMBED_HELP'),
          advanced: i18n.t('video.UPLOAD_OPTION_ADVANCED_HELP'),
          source: i18n.t('video.UPLOAD_FIELD_SOURCE_HELP'),
          alternativeSource: i18n.t(
            'video.UPLOAD_FIELD_ALTERNATIVE_SOURCE_HELP'
          ),
          poster: i18n.t('video.UPLOAD_FIELD_POSTER_HELP'),
        })

        editor.on('init', () => {
          lastSyncedValue = props.value ?? ''
          editor.setContent(lastSyncedValue)
        })

        // Listen for formatting commands like bold, italic, etc.
        editor.on('ExecCommand', () => {
          const content = editor.getContent()
          onInput(content)
        })

        // Listen for cursor changes:
        editor.on('NodeChange', () => {
          const content = editor.getContent()
          onInput(content)
        })

        editor.on('input', () => {
          const content = editor.getContent()
          onInput(content)
        })

        editor.on('PastePreProcess', (args) => {
          args.content = sanitizeRichText(args.content)
        })
      },
      ...configFile,
    })
  } catch (error) {
    Sentry.captureException(error)
  }
}

const onInput = (content: string) => {
  const sanitizedContent = sanitizeRichText(content)
  lastSyncedValue = sanitizedContent
  emit('input', sanitizedContent)
  emit('inputWithName', {
    name: props.name,
    value: sanitizedContent,
  })
}

function setAiButton(editor: Editor) {
  editor.ui.registry.addButton('aiButton', {
    icon: 'aiIcon',
    tooltip: 'AI Suggestions',
    onAction: () => {
      emit('onAi')
    },
  })
}

function setAiIcon(editor: Editor) {
  editor.ui.registry.addIcon('aiIcon', aiIconString)
}
</script>

<style lang="scss" scoped>
.label {
  @include formLabelStyle;
  display: flex;
  align-items: center;
  gap: $spacing-4xs;
}
</style>
